import { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { getQueryFn } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CountdownTimer from "@/components/countdown-timer";
import ReferFriend from "@/components/refer-friend";
import BetfredSignup from "@/components/betfred-signup";
import ShareAfterEntryPopup from "@/components/share-after-entry-popup";
import { CompactPrizeBadges } from "@/components/challenges/CompactPrizeBadges";
import { VerticalQuiz } from "@/components/mobile/VerticalQuiz";
import MobileCompetitionGuestView from "@/components/mobile/MobileCompetitionGuestView";
import { 
  Trophy, 
  Calendar, 
  Gift, 
  FileText,
  Play, 
  Clock, 
  Share2,
  Users,
  Target,
  Award,
  ArrowLeft,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  UserPlus,
  Send,
  Loader2
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { motion } from "framer-motion";

// Helper function to transform competition data to PrizeDetails format
function transformCompetitionToPrizeDetails(competition: any) {
  const prizeDetails: any = {};

  if (competition?.prize1) {
    prizeDetails["1st"] = { item: competition.prize1, value: 0 };
  }
  if (competition?.prize2) {
    prizeDetails["2nd"] = { item: competition.prize2, value: 0 };
  }
  if (competition?.prize3) {
    prizeDetails["3rd"] = { item: competition.prize3, value: 0 };
  }

  // Fallback for old single prize field
  if (
    !competition?.prize1 &&
    !competition?.prize2 &&
    !competition?.prize3 &&
    competition?.prize
  ) {
    prizeDetails["1st"] = { item: competition.prize, value: 0 };
  }

  return prizeDetails;
}

export default function MobileCompetitionDetail() {
  const params = useParams();
  const [, navigate] = useLocation();
  const { user, loading } = useAuth();
  const isAuthenticated = !!user;
  const { toast } = useToast();

  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showInviteFriendsModal, setShowInviteFriendsModal] = useState(false);
  const [shouldShowReferralPopup, setShouldShowReferralPopup] = useState(false);
  const [friendInvites, setFriendInvites] = useState([
    { id: 1, name: '', email: '', phone: '', sending: false, success: false }, // Added success flag
  ]);
  const [successfulInvitesCount, setSuccessfulInvitesCount] = useState(0); // State to track successful invites
  const [referralProcessed, setReferralProcessed] = useState(false);
  const [isQuizCollapsed, setIsQuizCollapsed] = useState(false);
  const [specialTermsAccepted, setSpecialTermsAccepted] = useState(false);

  const competitionId = parseInt((params as any).id as string);

  // Extract referral code from URL query parameters
  const referralCode = useMemo(() => {
    const currentUrl = window.location.href;
    const search = window.location.search;
    const urlParams = new URLSearchParams(search);
    const ref = urlParams.get("ref");

    // Debug logging for referral code extraction
    console.log('🔗 REFERRAL DEBUG - URL Analysis:', {
      fullUrl: currentUrl,
      searchParams: search,
      allParams: Object.fromEntries(urlParams.entries()),
      extractedRef: ref,
      hasRefParam: urlParams.has("ref"),
      allKeys: Array.from(urlParams.keys())
    });

    return ref;
  }, []);

  const {
    data: competition,
    isLoading,
    error,
  } = useQuery<any>({
    queryKey: ["/api/competitions", competitionId],
    retry: 3,
    retryDelay: 1000,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const { data: userEntries, refetch: refetchEntries } = useQuery<any>({
    queryKey: ["/api/user/entries", competitionId],
    enabled: isAuthenticated,
    staleTime: 0, // Always fetch fresh data to show correct total entries
    refetchOnWindowFocus: true, // Refetch on focus to get latest entry counts
    refetchOnMount: true, // Always refetch on mount
  });

  const { data: existingAnswers, refetch: refetchAnswers } = useQuery<any>({
    queryKey: ["/api/user/answers", competitionId],
    enabled: isAuthenticated,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const { data: referralData } = useQuery<any>({
    queryKey: ["/api/referral-code"],
    enabled: isAuthenticated,
  });

  const { data: globalStatus, error: globalStatusError } = useQuery<{ hasBeenVerified: boolean }>({
    queryKey: ["/api/betfred-global-status"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    staleTime: 0, // Always fresh data
    refetchOnWindowFocus: true,
    retry: 1, // Only retry once
  });

  // Debug logging
  console.log('🔍 Global Status Debug:', {
    isAuthenticated,
    globalStatus,
    error: globalStatusError,
    hasBeenVerified: globalStatus?.hasBeenVerified
  });


  // Calculate competition timing status
  const now = new Date();
  const startDateOnly = (competition as any)
    ? new Date((competition as any).startDate).toISOString().split("T")[0]
    : null;
  const startTimeStr = (competition as any)?.startTime || "00:00";
  const startDateTime = startDateOnly
    ? new Date(`${startDateOnly}T${startTimeStr}:00+02:00`)
    : null;

  const endDateOnly = (competition as any)
    ? new Date((competition as any).endDate).toISOString().split("T")[0]
    : null;
  const endTimeStr = (competition as any)?.endTime || "23:59";
  const endDateTime = endDateOnly
    ? new Date(`${endDateOnly}T${endTimeStr}:59+02:00`)
    : null;

  const isExpired = endDateTime ? endDateTime < now : false;
  const isScheduled = startDateTime ? startDateTime > now : false;

  const questions = competition?.questions || [];

  // Debug existing answers (after questions are defined)
  console.log('Existing answers data:', existingAnswers);
  console.log('Questions:', questions);

  const specialTermsStorageKey = competition?.id
    ? `specialTermsAccepted-${competition.id}`
    : null;

  const specialTermsParagraphs = useMemo(() => {
    if (!competition?.termsAndConditionsDialog) {
      return [];
    }

    return competition.termsAndConditionsDialog
      .split(/\r?\n/)
      .map((paragraph: string) => paragraph.trim())
      .filter(Boolean);
  }, [competition?.termsAndConditionsDialog]);

  useEffect(() => {
    if (!specialTermsStorageKey || typeof window === "undefined") {
      setSpecialTermsAccepted(false);
      return;
    }

    const storedValue = window.localStorage.getItem(specialTermsStorageKey);
    setSpecialTermsAccepted(storedValue === "true");
  }, [specialTermsStorageKey]);

  const showSpecialTermsCard =
    specialTermsParagraphs.length > 0 && !specialTermsAccepted;
  const showSpecialTermsAcceptedNotice =
    specialTermsParagraphs.length > 0 && specialTermsAccepted;

  // Check if user has answered all questions (at least one answer per unique question)
  const hasAnsweredAllQuestions = useMemo(() => {
    if (!existingAnswers || !Array.isArray(existingAnswers) || !questions || questions.length === 0) {
      return false;
    }

    // Get unique question IDs that have been answered
    const answeredQuestionIds = new Set(
      existingAnswers.map((answer: any) => answer.questionId)
    );

    // Check if all questions have at least one answer
    const allQuestionIds = new Set(questions.map((q: any) => q.id));

    return allQuestionIds.size === answeredQuestionIds.size && 
           [...allQuestionIds].every(id => answeredQuestionIds.has(id));
  }, [existingAnswers, questions]);

  // Check if user has already entered competition
  const hasEnteredCompetition = useMemo(() => {
    // Handle different userEntries formats: object with totalEntries, or array
    if (!userEntries) return false;

    // If it's an object with totalEntries property
    if (typeof userEntries === 'object' && 'totalEntries' in userEntries) {
      return (userEntries as any).totalEntries > 0;
    }

    // If it's an array or object with entries array
    if (Array.isArray(userEntries)) {
      return userEntries.length > 0;
    }

    if (userEntries && typeof userEntries === 'object' && 'entries' in userEntries) {
      return Array.isArray((userEntries as any).entries) && (userEntries as any).entries.length > 0;
    }

    return false;
  }, [userEntries]);

  // Get total entry count for display
  const totalEntryCount = useMemo(() => {
    if (!userEntries) return 0;

    // If it's an object with totalEntries property
    if (typeof userEntries === 'object' && 'totalEntries' in userEntries) {
      return (userEntries as any).totalEntries;
    }

    // If it's an array
    if (Array.isArray(userEntries)) {
      return userEntries.length;
    }

    // If it's an object with entries array
    if (userEntries && typeof userEntries === 'object' && 'entries' in userEntries) {
      const entries = (userEntries as any).entries;
      return Array.isArray(entries) ? entries.length : 0;
    }

    return 0;
  }, [userEntries]);


  // Handle competition entry and referral code processing when page loads
  useEffect(() => {
    // Debug logging for referral processing
    console.log('🔗 REFERRAL DEBUG - Entry Processing:', {
      isAuthenticated,
      referralCode,
      hasReferralCode: !!referralCode,
      referralProcessed,
      hasEnteredCompetition,
      isScheduled,
      competitionExists: !!competition,
      competitionId,
      userEntries,
      userEntriesType: typeof userEntries
    });

    // Don't process anything for unauthenticated users - let them login first
    if (!isAuthenticated) {
      console.log('🔗 REFERRAL DEBUG - User not authenticated, skipping referral processing');
      return;
    }

    // Return early if userEntries is still loading (undefined means query hasn't completed)
    if (userEntries === undefined) {
      console.log('🔗 REFERRAL DEBUG - User entries still loading, waiting...');
      return; // Wait for userEntries to load
    }

    // Only enter if user hasn't entered yet, competition exists, and not scheduled
    if (!referralProcessed && competition && !isScheduled && !hasEnteredCompetition) {
      console.log('🔗 REFERRAL DEBUG - Attempting to enter competition with referral:', {
        referralCode,
        competitionId,
        payload: { referralCode: referralCode || undefined }
      });

      apiRequest("POST", `/api/competitions/${competitionId}/enter`, {
        body: { referralCode: referralCode || undefined },
      })
        .then((response) => {
          console.log('🔗 REFERRAL DEBUG - Competition entry successful:', response);
          setReferralProcessed(true);
          queryClient.invalidateQueries({
            queryKey: ["/api/user/entries", competitionId],
          });
          queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });
          queryClient.invalidateQueries({ queryKey: ["/api/competitions"] });
          
          // Check if this is user's first competition entry overall
          const isFirstEverEntry = !userEntries || (Array.isArray(userEntries) ? userEntries.length === 0 : userEntries.totalEntries === 0);
          
          // Show appropriate popup based on entry status
          if (isFirstEverEntry) {
            // First time entering any competition - show share popup
            setTimeout(() => {
              setShowSharePopup(true);
            }, 1000);
          } else {
            // Subsequent competition entries - show invite friends modal
            setTimeout(() => {
              setShowInviteFriendsModal(true);
            }, 1000);
          }
        })
        .catch((error) => {
          console.error("🔗 REFERRAL DEBUG - Error entering competition:", error);
          setReferralProcessed(true);
        });
    } else if (isScheduled || hasEnteredCompetition) {
      console.log('🔗 REFERRAL DEBUG - Skipping entry (scheduled or already entered):', {
        isScheduled,
        hasEnteredCompetition
      });
      // Mark as processed if user has already entered or competition is scheduled
      setReferralProcessed(true);
    }
  }, [isAuthenticated, competitionId, referralProcessed, competition, isScheduled, hasEnteredCompetition, referralCode]);

  // Auto-collapse quiz when all questions are answered
  useEffect(() => {
    if (hasAnsweredAllQuestions && !isQuizCollapsed) {
      setIsQuizCollapsed(true);
    }
  }, [hasAnsweredAllQuestions]);

  const submitAnswersMutation = useMutation({
    mutationFn: async (data: { answers: any[] }) => {
      return await apiRequest(
        "POST",
        `/api/competitions/${competitionId}/submit`,
        { body: data },
      );
    },
    onSuccess: (data: any) => {
      const entriesEarned = data?.entriesCreated || data?.answers?.length || 0;
      
      // Only show success toast if entries were actually earned
      if (entriesEarned > 0) {
        toast({
          title: "Success!",
          description: `You earned ${entriesEarned} new ${entriesEarned === 1 ? "entry" : "entries"}! Each answer = 1 entry.`,
        });
      }

      queryClient.removeQueries({
        queryKey: ["/api/competitions", competitionId],
      });
      queryClient.removeQueries({
        queryKey: ["/api/user/entries", competitionId],
      });
      queryClient.removeQueries({
        queryKey: ["/api/user/answers", competitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/competitions", competitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/entries", competitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/answers", competitionId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });

      refetchAnswers();
      refetchEntries();

      const isFirstEntry = data?.isFirstEntry === true;
      const isFirstEverEntry = data?.isFirstEverEntry === true;
      
      // Clear only the submitted answers from local state
      setAnswers(prev => {
        const newAnswers = { ...prev };
        if (data?.answers) {
          data.answers.forEach((answer: any) => {
            delete newAnswers[answer.questionId];
          });
        }
        return newAnswers;
      });

      // Show appropriate popup based on entry status
      if (isFirstEverEntry) {
        // Very first competition entry ever - show share popup
        setTimeout(() => {
          setShowSharePopup(true);
        }, 1000);
      } else if (isFirstEntry) {
        // First entry to this specific competition - show invite friends modal
        setTimeout(() => {
          setShowInviteFriendsModal(true);
        }, 1000);
      }
    },
    onError: (error: any) => {
      console.error("Submit error:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit answers. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAnswerChange = (questionId: number, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = () => {
    if (!isAuthenticated) {
      navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    // Create a map of existing answers to avoid re-submitting
    const existingAnswerMap = new Map();
    if (existingAnswers && Array.isArray(existingAnswers)) {
      existingAnswers.forEach((answer: any) => {
        existingAnswerMap.set(answer.questionId, answer.answerText || answer.answer);
      });
    }

    // Only submit new answers that haven't been submitted before
    const newAnswers = Object.entries(answers)
      .filter(([questionId, answer]) => {
        const qId = parseInt(questionId);
        return !existingAnswerMap.has(qId);
      })
      .map(([questionId, answer]) => ({
        questionId: parseInt(questionId),
        answerText: answer
      }));

    if (newAnswers.length === 0) {
      toast({
        title: "No New Answers",
        description: "Please answer new questions to earn more entries.",
        variant: "destructive",
      });
      return;
    }

    console.log('Submitting new answers:', newAnswers);
    console.log('Competition ID:', competitionId);
    console.log('User authenticated:', isAuthenticated);

      submitAnswersMutation.mutate({ answers: newAnswers });
  };

  const handleAcceptSpecialTerms = () => {
    if (!specialTermsStorageKey || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(specialTermsStorageKey, "true");
    setSpecialTermsAccepted(true);
  };

  const handleReviewSpecialTerms = () => {
    if (!specialTermsStorageKey || typeof window === "undefined") {
      return;
    }
    window.localStorage.removeItem(specialTermsStorageKey);
    setSpecialTermsAccepted(false);
  };

  // Handle sending friend invite to PrizeDrop
  const handleSendInvite = async (index: number) => {
    const friend = friendInvites[index];

    // Validate form fields
    if (!friend.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter your friend's name",
        variant: "destructive",
      });
      return;
    }

    if (!friend.phone.trim()) {
      toast({
        title: "Error",
        description: "Please enter your friend's phone number",
        variant: "destructive",
      });
      return;
    }

    // Set sending state for this row
    setFriendInvites(prev => prev.map((f, i) => 
      i === index ? { ...f, sending: true, success: false } : f // Reset success on new send attempt
    ));

    try {
      await apiRequest('POST', '/api/friends/invite-prizedrop', {
        body: {
          friendName: friend.name,
          friendEmail: friend.email || undefined,
          friendPhone: friend.phone,
          competitionId: competitionId, // Send competition-specific referral link
        },
      });

      toast({
        title: "Invitation Sent!",
        description: `${friend.name} will receive an SMS invitation to join this competition`,
      });

      // Remove the successfully sent invite row to show a clean form
      setFriendInvites(prev => {
        const updated = prev.filter((_, i) => i !== index);
        // If all invites were removed, add a fresh empty one
        if (updated.length === 0) {
          return [{ id: 1, name: '', email: '', phone: '', sending: false, success: false }];
        }
        return updated;
      });
      setSuccessfulInvitesCount(prev => prev + 1);

    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
      // Keep fields populated on error but reset sending state
      setFriendInvites(prev => prev.map((f, i) => 
        i === index ? { ...f, sending: false } : f
      ));
    }
  };

  // Update friend invite field
  const updateFriendInvite = (index: number, field: 'name' | 'email' | 'phone', value: string) => {
    setFriendInvites(prev => prev.map((f, i) => 
      i === index ? { ...f, [field]: value, success: false } : f // Reset success on edit
    ));
  };

  // Clear all fields after all invites are processed (or user closes modal)
  const resetInviteForm = () => {
    setFriendInvites([{ id: 1, name: '', email: '', phone: '', sending: false, success: false }]);
    setSuccessfulInvitesCount(0);
  };

  // Handle modal close with cleanup
  const handleInviteModalClose = () => {
    setShowInviteFriendsModal(false);
    resetInviteForm();
  };

  if (loading || isLoading) {
    return <MobileCompetitionDetailSkeleton />;
  }

  if (error || !competition) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
        <div className="px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Competition Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The competition you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/m/competitions")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Competitions
          </Button>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show the guest view
  if (!isAuthenticated) {
    return (
      <MobileCompetitionGuestView
        competition={competition}
        competitionId={competitionId}
        referralCode={referralCode || undefined}
      />
    );
  }

  const answeredQuestions = existingAnswers?.length || 0;
  const totalQuestions = questions.length;
  const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const prizeDetails = transformCompetitionToPrizeDetails(competition);

  return (
    <div 
      className="min-h-screen w-full max-w-full bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden"
      style={{ 
        paddingBottom: 'max(6rem, calc(6rem + env(safe-area-inset-bottom)))',
        minWidth: '100vw',
        width: '100vw'
      }}
    >
      {/* Mobile Header */}
      <div 
        className="text-white"
        style={{ 
          background: 'linear-gradient(to right, #0e1b90, #1e2bb0)',
          paddingTop: 'max(3rem, calc(3rem + env(safe-area-inset-top)))' 
        }}
      >
        <div className="px-4 pb-6">
          <div className="flex items-center mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/m/competitions")}
              className="text-white hover:bg-white/20 mr-3 p-2"
              data-testid="button-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold line-clamp-2">{competition.title}</h1>
              <div className="flex items-center space-x-4 mt-1">
                <Badge 
                  className={`text-xs ${isExpired ? 'bg-red-600' : isScheduled ? 'bg-yellow-600' : 'bg-green-600'}`}
                >
                  {isExpired ? 'Ended' : isScheduled ? 'Scheduled' : 'Active'}
                </Badge>
                {!isExpired && !isScheduled && endDateTime && (
                  <div className="text-xs text-white">
                    <CountdownTimer 
                      endDate={competition.endDate}
                      endTime={competition.endTime}
                      compact={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Action Buttons - Only show for authenticated users */}
          {isAuthenticated && referralData && (
            <div className="flex flex-col space-y-2 px-4">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                onClick={() => setShowInviteFriendsModal(true)}
                disabled={!(referralData as any)?.referralUrl}
                data-testid="button-invite-friends"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite your friends
              </Button>
              <Button
                variant="outline"
                className="w-full bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-700 py-3"
                onClick={() => {
                  const referFriendSection = document.getElementById('refer-friend-section');
                  if (referFriendSection) {
                    referFriendSection.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                      inline: 'nearest',
                    });
                  }
                }}
                disabled={!(referralData as any)?.referralUrl}
                data-testid="button-share-referral"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share Referral Link to Socials
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Betfred CTA Button - Mobile */}
      {isAuthenticated && !globalStatus?.hasBeenVerified && (
        <div className="px-4 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <Button
            variant="default"
            size="lg"
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg font-semibold py-4"
            onClick={() => {
              // Scroll to the Betfred section
              const betfredSection = document.querySelector('[data-testid="betfred-signup-section"]');
              if (betfredSection) {
                betfredSection.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }}
            data-testid="button-get-entries"
          >
            <Gift className="mr-2 h-5 w-5" />
            CLICK HERE TO GET 50 ENTRIES NOW
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="w-full max-w-full px-4 py-6 space-y-8">
        {showSpecialTermsCard && (
          <Card className="rounded-2xl shadow-lg border-2">
            <CardHeader className="pb-3">
              <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-gray-700" />
                <span className="font-semibold">Special Terms &amp; Conditions</span>
                <div className="ml-auto flex flex-shrink-0">
                  <Button
                    onClick={handleAcceptSpecialTerms}
                    size="sm"
                    variant="outline"
                  >
                    Accept Terms
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800 text-sm">Special Terms Apply</p>
                    <p className="text-xs text-amber-700">
                      This competition has specific terms and conditions that you must read and accept before participating.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto text-sm text-gray-700 space-y-3">
                {specialTermsParagraphs.map((paragraph, index) => (
                  <p key={index} className="leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {showSpecialTermsAcceptedNotice && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>Special terms accepted for this competition.</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReviewSpecialTerms}
              >
                View Terms Again
              </Button>
            </div>
          </div>
        )}
        {/* Interactive Quiz Section - Only show for authenticated users */}
        {isAuthenticated && totalQuestions > 0 && !isExpired && (
          <Card id="enter-competition" className="rounded-2xl shadow-lg border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center">
                  {hasAnsweredAllQuestions ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2 text-green-600" />
                      Quiz Completed
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5 mr-2 text-green-600" />
                      {isScheduled ? 'Questions Preview' : 'Interactive Quiz'}
                    </>
                  )}
                </div>
                {hasAnsweredAllQuestions && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsQuizCollapsed(!isQuizCollapsed)}
                    data-testid="button-toggle-quiz"
                  >
                    {isQuizCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </Button>
                )}
              </CardTitle>
            </CardHeader>

            {!isQuizCollapsed && (
              <CardContent className="px-2">
                <VerticalQuiz
                  questions={questions}
                  answers={answers}
                  onAnswerChange={handleAnswerChange}
                  onSubmit={handleSubmit}
                  isSubmitting={submitAnswersMutation.isPending}
                  disabled={isScheduled}
                  existingAnswers={existingAnswers || []}
                  data-testid="vertical-quiz"
                />
              </CardContent>
            )}

            {isQuizCollapsed && hasAnsweredAllQuestions && (
              <CardContent>
                <div className="text-center py-4">
                  <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    All {questions.length} questions answered
                  </Badge>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Tap to review your answers
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Your Entries Section (if authenticated and has questions) */}
        {isAuthenticated && totalQuestions > 0 && (
          <Card className="rounded-2xl shadow-lg border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <Trophy className="h-5 w-5 mr-2 text-blue-600" />
                Your Entries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Total Entries Display */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Total Entries:
                    </span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {totalEntryCount}
                    </span>
                  </div>
                </div>

                {/* Entries Breakdown */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">
                      • {answeredQuestions} {answeredQuestions === 1 ? 'question' : 'questions'} completed
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {answeredQuestions} {answeredQuestions === 1 ? 'entry' : 'entries'}
                    </span>
                  </div>

                  {(() => {
                    const entryTypesCounts = (
                      (userEntries as any)?.entries || []
                    ).reduce(
                      (acc: Record<string, number>, entry: any) => {
                        acc[entry.entryType] =
                          (acc[entry.entryType] || 0) + 1;
                        return acc;
                      },
                      {},
                    );

                    return (
                      <>
                        {entryTypesCounts.referral && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-gray-600 dark:text-gray-400">
                              • Referral bonus entries
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {entryTypesCounts.referral}
                            </span>
                          </div>
                        )}
                        {entryTypesCounts.betfred_signup && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-gray-600 dark:text-gray-400">
                              • Betfred signup bonus
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {entryTypesCounts.betfred_signup}
                            </span>
                          </div>
                        )}
                        {entryTypesCounts.share && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-gray-600 dark:text-gray-400">
                              • Social share bonus
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {entryTypesCounts.share}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Progress Bar */}
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>Questions Progress</span>
                    <span>{answeredQuestions} of {totalQuestions}</span>
                  </div>
                  <Progress value={answeredQuestions > 0 ? progressPercentage : 0} className="h-2" />
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    {answeredQuestions > 0 ? progressPercentage.toFixed(0) : 0}% complete
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prize Section */}
        <Card className="rounded-2xl shadow-lg border-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <Gift className="h-5 w-5 mr-2 text-yellow-600" />
              Prizes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CompactPrizeBadges prizeDetails={prizeDetails} />
          </CardContent>
        </Card>

        {/* Description */}
        {competition.description && (
          <Card className="rounded-2xl shadow-lg border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">About This Competition</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {competition.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Additional features for authenticated users only */}
        {isAuthenticated && (
          <>
            {/* Betfred Signup Section */}
            {!globalStatus?.hasBeenVerified && (
              <div data-testid="betfred-signup-section">
                <BetfredSignup 
                  competitionId={competitionId}
                  title={competition?.title || 'Competition'}
                />
              </div>
            )}

            {/* Refer Friends Section */}
            {referralData && (
              <div id="refer-friend-section">
                <ReferFriend
                  competitionId={competitionId}
                  title={competition?.title || 'Competition'}
                />
              </div>
            )}
          </>
        )}

        {/* Ended Competition Message */}
        {isExpired && (
          <Card>
            <CardContent className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Competition Ended
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                This competition has ended. Check back for new competitions!
              </p>
              <Button onClick={() => navigate("/m/competitions")}>
                View Active Competitions
              </Button>
            </CardContent>
          </Card>
        )}
      </div>


      {/* Popups */}
      <ShareAfterEntryPopup
        isOpen={showSharePopup}
        onClose={() => setShowSharePopup(false)}
        competitionTitle={competition.title}
        competitionId={competitionId}
      />

      {/* Invite Friend to PrizeDrop Modal */}
      <Dialog open={showInviteFriendsModal} onOpenChange={(open) => {
        if (!open) {
          handleInviteModalClose();
        }
      }}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              Invite a friend and both of you earn 35 bonus entries
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Header row - Hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-sm font-medium text-gray-700">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email (Optional)</div>
              <div className="col-span-3">Phone Number</div>
              <div className="col-span-3">Status</div>
            </div>

            {/* Friend invite rows */}
            {friendInvites.map((friend, index) => (
              <div key={friend.id} className="space-y-3 md:space-y-0">
                {/* Mobile Layout */}
                <div className="block md:hidden space-y-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="text-sm font-medium text-gray-700">
                    Friend {index + 1} {friend.success && <CheckCircle className="h-4 w-4 text-green-500 inline-block ml-1" />}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${friend.success ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                      placeholder="John Doe"
                      value={friend.name}
                      onChange={(e) => updateFriendInvite(index, 'name', e.target.value)}
                      data-testid={`input-friend-name-${index}`}
                      disabled={friend.sending || friend.success}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${friend.success ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                      placeholder="27123456789"
                      value={friend.phone}
                      onChange={(e) => updateFriendInvite(index, 'phone', e.target.value)}
                      data-testid={`input-friend-phone-${index}`}
                      disabled={friend.sending || friend.success}
                    />
                  </div>

                  <Button
                    className="w-full h-10"
                    onClick={() => handleSendInvite(index)}
                    disabled={friend.sending || friend.success || !friend.name.trim() || !friend.phone.trim()}
                    data-testid={`button-send-invite-${index}`}
                    variant={friend.success ? "success" : "default"}
                  >
                    {friend.sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : friend.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Sent!
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${friend.success ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                    placeholder="John Doe"
                    value={friend.name}
                    onChange={(e) => updateFriendInvite(index, 'name', e.target.value)}
                    data-testid={`input-friend-name-${index}`}
                    disabled={friend.sending || friend.success}
                  />

                  <input
                    type="email"
                    className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${friend.success ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                    placeholder="friend@example.com"
                    value={friend.email}
                    onChange={(e) => updateFriendInvite(index, 'email', e.target.value)}
                    data-testid={`input-friend-email-${index}`}
                    disabled={friend.sending || friend.success}
                  />

                  <input
                    type="tel"
                    className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${friend.success ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                    placeholder="27123456789"
                    value={friend.phone}
                    onChange={(e) => updateFriendInvite(index, 'phone', e.target.value)}
                    data-testid={`input-friend-phone-${index}`}
                    disabled={friend.sending || friend.success}
                  />

                  <Button
                    className="col-span-3"
                    onClick={() => handleSendInvite(index)}
                    disabled={friend.sending || friend.success || !friend.name.trim() || !friend.phone.trim()}
                    data-testid={`button-send-invite-${index}`}
                    variant={friend.success ? "success" : "default"}
                  >
                    {friend.sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : friend.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Sent!
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Invite
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}

          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            {successfulInvitesCount > 0 && (
              <div className="mr-auto text-sm text-green-600 font-medium">
                <CheckCircle2 className="h-4 w-4 inline-block mr-1" />
                {successfulInvitesCount} invite(s) sent successfully!
              </div>
            )}
            <Button
              variant="outline"
              onClick={handleInviteModalClose}
              className="w-full sm:w-auto h-12"
              data-testid="button-close-modal"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MobileCompetitionDetailSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Header Skeleton */}
      <div className="text-white px-4 pt-12 pb-6" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="flex items-center space-x-3">
          <Skeleton className="h-8 w-8 bg-white/20" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-3/4 bg-white/20" />
            <Skeleton className="h-4 w-1/2 bg-white/20" />
          </div>
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="px-4 py-6 space-y-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

    </div>
  );
}
